export interface MapAnchor extends Record<string, unknown> {
  id: string;
  locationDocumentId: string;
  /** Studio link to a separate canonical map; browsing does not move the player. */
  targetMapId?: string;
  label: string;
  x: number;
  y: number;
  layerId: string;
  visibility: "known" | "discovered" | "hidden";
}

export interface MapAreaVertex {
  x: number;
  y: number;
}

export interface MapAreaStyle {
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeWidth?: number;
}

export interface MapArea extends Record<string, unknown> {
  id: string;
  layerId: string;
  locationDocumentId: string;
  label?: string;
  vertices: MapAreaVertex[];
  style?: MapAreaStyle;
}

export interface MapLayer extends Record<string, unknown> {
  id: string;
  name: string;
  imageAssetId: string;
  layerKind?: "world" | "region" | "city" | "interior";
  parentLayerId?: string;
  scaleLabel?: string;
  unitLabel?: string;
  widthUnits?: number;
  heightUnits?: number;
}

export interface MapTravelRule extends Record<string, unknown> {
  mode: string;
  unitsPerHour: number;
  requirements?: string[];
}

export interface MapRoute extends Record<string, unknown> {
  id: string;
  fromAnchorId: string;
  toAnchorId: string;
  distanceUnits: number;
  terrainMultiplier: number;
  bidirectional: boolean;
  allowedModes: string[];
  notes?: string;
}

export interface MapToken extends Record<string, unknown> {
  id: string;
  label: string;
  slug: string;
  layerId: string;
  x: number;
  y: number;
  link?: string | undefined;
  style?: { color?: string; icon?: string } | undefined;
}

export interface StoryMapDocument extends Record<string, unknown> {
  rootLocationId?: string;
  id: string;
  storyId: string;
  title: string;
  unitLabel: string;
  layers: MapLayer[];
  anchors: MapAnchor[];
  tokens?: MapToken[];
  areas?: MapArea[];
  routes: MapRoute[];
  travelRules: MapTravelRule[];
  revision: number;
}

/** Validate canonical identities separately from drawing geometry. Unlinked draft points are allowed. */
export function validateMapFileBindings(map: StoryMapDocument, locationIds: ReadonlySet<string>, maps: readonly StoryMapDocument[]): string[] {
  const errors: string[] = [];
  if (!map.rootLocationId || !locationIds.has(map.rootLocationId)) errors.push('Map requires an existing location file as rootLocationId');
  for (const layer of map.layers) {
    if (layer.locationDocumentId && layer.locationDocumentId !== map.rootLocationId) errors.push(`Layer ${layer.id} must use its map owner; link a separate place through an anchor`);
  }
  for (const anchor of map.anchors) {
    if (anchor.locationDocumentId && !locationIds.has(anchor.locationDocumentId)) errors.push(`Anchor ${anchor.id} references a missing location file`);
    if (anchor.targetMapId) {
      const target = maps.find(item => item.id === anchor.targetMapId);
      if (!anchor.locationDocumentId || !target || target.id === map.id || target.rootLocationId !== anchor.locationDocumentId) errors.push(`Anchor ${anchor.id} must link to the map of its selected location file`);
    }
  }
  return errors;
}

export function calculateTravelDuration(
  distanceUnits: number,
  terrainMultiplier = 1,
  unitsPerHour = 4
): { hours: number; minutes: number; totalMinutes: number; formatted: string } {
  const effectiveSpeed = Math.max(0.01, unitsPerHour);
  const effectiveDistance = Math.max(0, distanceUnits * Math.max(0.1, terrainMultiplier));
  const totalHours = effectiveDistance / effectiveSpeed;
  const totalMinutes = Math.round(totalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const formatted = hours > 0
    ? minutes > 0 ? `${hours} ชม. ${minutes} นาที` : `${hours} ชม.`
    : `${minutes} นาที`;
  return { hours, minutes, totalMinutes, formatted };
}

const mapObject = (value: unknown): Record<string, unknown> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const mapString = (value: unknown, fallback = "") => typeof value === "string" ? value : fallback;
const mapNumber = (value: unknown, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const mapStrings = (value: unknown) => Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

export function normalizeStoryMap(value: unknown, storyId = "", index = 0): StoryMapDocument {
  const source = mapObject(value);
  const layers = Array.isArray(source.layers) ? source.layers.map((value, layerIndex): MapLayer => {
    const layer = mapObject(value);
    const result: MapLayer = {
      ...layer,
      id: mapString(layer.id, `layer-${layerIndex + 1}`),
      name: mapString(layer.name, `ชั้นแผนที่ ${layerIndex + 1}`),
      imageAssetId: mapString(layer.imageAssetId) || (layerIndex === 0 && source.imageRole === "structural" ? mapString(source.image) : ""),
      scaleLabel: mapString(layer.scaleLabel),
      widthUnits: Math.max(1, mapNumber(layer.widthUnits, 100)),
      heightUnits: Math.max(1, mapNumber(layer.heightUnits, 100)),
    };
    if (typeof layer.parentLayerId === "string" && layer.parentLayerId) result.parentLayerId = layer.parentLayerId;
    if (typeof layer.layerKind === "string" && ["world", "region", "city", "interior"].includes(layer.layerKind)) {
      result.layerKind = layer.layerKind as "world" | "region" | "city" | "interior";
    }
    return result;
  }) : [];
  const layerId = layers[0]?.id ?? "layer-1";
  const anchors = Array.isArray(source.anchors) ? source.anchors.map((value, anchorIndex): MapAnchor => {
    const anchor = mapObject(value);
    const visibility = anchor.visibility === "hidden" || anchor.visibility === "discovered" ? anchor.visibility : "known";
    return {
      ...anchor,
      id: mapString(anchor.id, `anchor-${anchorIndex + 1}`),
      locationDocumentId: mapString(anchor.locationDocumentId),
      label: mapString(anchor.label, `จุด ${anchorIndex + 1}`),
      x: Math.max(0, Math.min(1, mapNumber(anchor.x, .5))),
      y: Math.max(0, Math.min(1, mapNumber(anchor.y, .5))),
      layerId: mapString(anchor.layerId, layerId),
      visibility,
    };
  }) : [];
  const areas = Array.isArray(source.areas) ? source.areas.map((value, areaIndex): MapArea => {
    const area = mapObject(value);
    const vertices = Array.isArray(area.vertices) ? area.vertices.map((v) => {
      const vertex = mapObject(v);
      return {
        x: Math.max(0, Math.min(1, mapNumber(vertex.x, 0))),
        y: Math.max(0, Math.min(1, mapNumber(vertex.y, 0))),
      };
    }) : [];
    const style = mapObject(area.style);
    return {
      ...area,
      id: mapString(area.id, `area-${areaIndex + 1}`),
      layerId: mapString(area.layerId, layerId),
      locationDocumentId: mapString(area.locationDocumentId),
      label: mapString(area.label),
      vertices,
      style: {
        fillColor: mapString(style.fillColor, "#22c55e"),
        fillOpacity: Math.max(0, Math.min(1, mapNumber(style.fillOpacity, 0.35))),
        strokeColor: mapString(style.strokeColor, "#22c55e"),
        strokeWidth: Math.max(0.5, mapNumber(style.strokeWidth, 2)),
      },
    };
  }) : [];
  const routes = Array.isArray(source.routes) ? source.routes.map((value, routeIndex): MapRoute => {
    const route = mapObject(value);
    return {
      ...route,
      id: mapString(route.id, `route-${routeIndex + 1}`),
      fromAnchorId: mapString(route.fromAnchorId),
      toAnchorId: mapString(route.toAnchorId),
      distanceUnits: Math.max(.01, mapNumber(route.distanceUnits, 1)),
      terrainMultiplier: Math.max(.01, mapNumber(route.terrainMultiplier, 1)),
      bidirectional: route.bidirectional !== false,
      allowedModes: mapStrings(route.allowedModes).length ? mapStrings(route.allowedModes) : ["เดิน"],
      notes: mapString(route.notes),
    };
  }) : [];
  const tokens = Array.isArray(source.tokens) ? source.tokens.map((value, tokenIndex): MapToken => {
    const token = mapObject(value);
    const slug = mapString(token.slug) || mapString(token.label).toLowerCase().replace(/\s+/g, "-") || `token-${tokenIndex + 1}`;
    return {
      ...token,
      id: mapString(token.id, `token-${tokenIndex + 1}`),
      label: mapString(token.label, `Token ${tokenIndex + 1}`),
      slug,
      layerId: mapString(token.layerId, layerId),
      x: Math.max(0, Math.min(1, mapNumber(token.x, 0.5))),
      y: Math.max(0, Math.min(1, mapNumber(token.y, 0.5))),
      ...(typeof token.link === "string" ? { link: token.link } : {}),
      ...(token.style && typeof token.style === "object" ? { style: token.style as { color?: string; icon?: string } } : {}),
    };
  }) : [];
  const travelRules = Array.isArray(source.travelRules) ? source.travelRules.map((value): MapTravelRule => {
    const rule = mapObject(value);
    return {
      ...rule,
      mode: mapString(rule.mode, "เดิน"),
      unitsPerHour: Math.max(.01, mapNumber(rule.unitsPerHour, 4)),
      requirements: mapStrings(rule.requirements),
    };
  }) : [];
  return {
    ...source,
    id: mapString(source.id, `map-${index + 1}`),
    storyId: mapString(source.storyId, storyId),
    title: mapString(source.title, `แผนที่ ${index + 1}`),
    unitLabel: mapString(source.unitLabel, "กม."),
    layers,
    anchors,
    tokens,
    areas,
    routes,
    travelRules,
    revision: Math.max(1, Math.trunc(mapNumber(source.revision, 1))),
  };
}

export function createEmptyStoryMap(storyId: string, id: string, title = "แผนที่โลก"): StoryMapDocument {
  return normalizeStoryMap({
    id,
    storyId,
    title,
    unitLabel: "กม.",
    layers: [{ id: crypto.randomUUID(), name: "โลกหลัก", imageAssetId: "", widthUnits: 100, heightUnits: 100, scaleLabel: "100 กม.", layerKind: "world" }],
    anchors: [],
    tokens: [],
    areas: [],
    routes: [],
    travelRules: [{ mode: "เดิน", unitsPerHour: 4 }, { mode: "พาหนะ", unitsPerHour: 12 }],
    revision: 1,
  }, storyId);
}

export function validateMapPolygon(vertices: MapAreaVertex[]): string[] {
  const errors: string[] = [];
  if (!vertices || vertices.length < 3) {
    errors.push("polygon requires at least 3 vertices");
    return errors;
  }
  for (const v of vertices) {
    if (typeof v.x !== "number" || typeof v.y !== "number" || !Number.isFinite(v.x) || !Number.isFinite(v.y) || v.x < 0 || v.x > 1 || v.y < 0 || v.y > 1) {
      errors.push("vertices must be within 0..1");
      break;
    }
  }
  for (let i = 0; i < vertices.length; i++) {
    for (let j = i + 1; j < vertices.length; j++) {
      if (Math.abs(vertices[i]!.x - vertices[j]!.x) < 1e-9 && Math.abs(vertices[i]!.y - vertices[j]!.y) < 1e-9) {
        errors.push("duplicate vertex");
        break;
      }
    }
    if (errors.includes("duplicate vertex")) break;
  }
  let area2 = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area2 += vertices[i]!.x * vertices[j]!.y - vertices[j]!.x * vertices[i]!.y;
  }
  if (Math.abs(area2) < 1e-6) {
    errors.push("polygon has no area");
  }

  const ccw = (a: MapAreaVertex, b: MapAreaVertex, c: MapAreaVertex) => (c.y - a.y) * (b.x - a.x) > (b.y - a.y) * (c.x - a.x);
  const intersect = (p1: MapAreaVertex, p2: MapAreaVertex, p3: MapAreaVertex, p4: MapAreaVertex) => {
    return ccw(p1, p3, p4) !== ccw(p2, p3, p4) && ccw(p1, p2, p3) !== ccw(p1, p2, p4);
  };
  for (let i = 0; i < n; i++) {
    const p1 = vertices[i]!;
    const p2 = vertices[(i + 1) % n]!;
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      const p3 = vertices[j]!;
      const p4 = vertices[(j + 1) % n]!;
      if (intersect(p1, p2, p3, p4)) {
        errors.push("edges intersect");
        break;
      }
    }
    if (errors.includes("edges intersect")) break;
  }

  return errors;
}

export function validateStoryMap(map: StoryMapDocument): string[] {
  const errors: string[] = [];
  const layerIds = new Set<string>();
  for (const layer of map.layers) {
    if (!layer.id || !layer.imageAssetId) errors.push("map layers require id and imageAssetId");
    if (layerIds.has(layer.id)) errors.push(`duplicate layer: ${layer.id}`);
    layerIds.add(layer.id);
  }
  for (const layer of map.layers) {
    if (layer.parentLayerId) {
      let curr = layer.parentLayerId;
      const seen = new Set<string>([layer.id]);
      while (curr) {
        if (seen.has(curr)) {
          errors.push(`layer ${layer.id} has a cyclic parent`);
          break;
        }
        seen.add(curr);
        const parent = map.layers.find((l) => l.id === curr);
        curr = parent?.parentLayerId ?? "";
      }
    }
  }
  for (const area of map.areas ?? []) {
    if (!layerIds.has(area.layerId)) {
      errors.push(`area ${area.id} references a missing layer`);
    }
  }
  const anchors = new Set<string>();
  for (const anchor of map.anchors) {
    if (!layerIds.has(anchor.layerId)) errors.push(`anchor ${anchor.id} references a missing layer`);
    if (anchor.x < 0 || anchor.x > 1 || anchor.y < 0 || anchor.y > 1) errors.push(`anchor ${anchor.id} is outside the image`);
    if (anchors.has(anchor.id)) errors.push(`duplicate anchor: ${anchor.id}`);
    anchors.add(anchor.id);
  }
  for (const route of map.routes) {
    if (!anchors.has(route.fromAnchorId) || !anchors.has(route.toAnchorId)) errors.push(`route ${route.id} references a missing anchor`);
    if (!(route.distanceUnits > 0) || !(route.terrainMultiplier > 0)) errors.push(`route ${route.id} needs positive distance and terrain`);
  }
  for (const rule of map.travelRules) if (!(rule.unitsPerHour > 0)) errors.push(`travel mode ${rule.mode} needs positive speed`);
  return errors;
}

export function estimateTravelHours(route: MapRoute, rule: MapTravelRule): number {
  if (route.distanceUnits <= 0 || route.terrainMultiplier <= 0 || rule.unitsPerHour <= 0) throw new Error("invalid travel inputs");
  if (!route.allowedModes.includes(rule.mode)) throw new Error(`${rule.mode} cannot use route ${route.id}`);
  return route.distanceUnits * route.terrainMultiplier / rule.unitsPerHour;
}

export interface PlaceTokenOptions {
  label: string;
  slug?: string;
  layerId?: string;
  x: number; // 0.0 to 1.0
  y: number; // 0.0 to 1.0
  link?: string;
  style?: { color?: string; icon?: string };
}

/** Places or updates a token on the map with normalized coordinates */
export function placeMapToken(map: StoryMapDocument, opts: PlaceTokenOptions): { map: StoryMapDocument; token: MapToken } {
  const currentTokens = [...(map.tokens ?? [])];
  const slug = opts.slug || opts.label.toLowerCase().replace(/\s+/g, "-");
  const layerId = opts.layerId || map.layers[0]?.id || "layer-1";
  const existingIdx = currentTokens.findIndex((t) => t.slug === slug || t.label.toLowerCase() === opts.label.toLowerCase());

  const token: MapToken = {
    id: existingIdx >= 0 ? currentTokens[existingIdx]!.id : `token-${crypto.randomUUID()}`,
    label: opts.label,
    slug,
    layerId,
    x: Math.max(0, Math.min(1, opts.x)),
    y: Math.max(0, Math.min(1, opts.y)),
    ...(opts.link ? { link: opts.link } : {}),
    ...(opts.style ? { style: opts.style } : {}),
  };

  if (existingIdx >= 0) {
    currentTokens[existingIdx] = token;
  } else {
    currentTokens.push(token);
  }

  return {
    map: { ...map, tokens: currentTokens, revision: map.revision + 1 },
    token,
  };
}

/** Moves an existing token to new normalized coordinates */
export function moveMapToken(map: StoryMapDocument, slugOrId: string, position: { x: number; y: number }): { map: StoryMapDocument; token?: MapToken } {
  const currentTokens = [...(map.tokens ?? [])];
  const targetLower = slugOrId.toLowerCase();
  const idx = currentTokens.findIndex((t) => t.slug.toLowerCase() === targetLower || t.id === slugOrId || t.label.toLowerCase() === targetLower);

  if (idx < 0) return { map };

  const updatedToken: MapToken = {
    ...currentTokens[idx]!,
    x: Math.max(0, Math.min(1, position.x)),
    y: Math.max(0, Math.min(1, position.y)),
  };
  currentTokens[idx] = updatedToken;

  return {
    map: { ...map, tokens: currentTokens, revision: map.revision + 1 },
    token: updatedToken,
  };
}

/** Removes a token from the map */
export function removeMapToken(map: StoryMapDocument, slugOrId: string): StoryMapDocument {
  const targetLower = slugOrId.toLowerCase();
  const currentTokens = (map.tokens ?? []).filter((t) => t.slug.toLowerCase() !== targetLower && t.id !== slugOrId && t.label.toLowerCase() !== targetLower);
  return { ...map, tokens: currentTokens, revision: map.revision + 1 };
}

export interface MapSearchResult {
  kind: "token" | "anchor" | "area";
  id: string;
  label: string;
  x?: number;
  y?: number;
}

/** Searches tokens, anchors, and areas on the map */
export function searchStoryMap(map: StoryMapDocument, query: string): MapSearchResult[] {
  const lower = query.toLowerCase();
  const results: MapSearchResult[] = [];

  for (const token of map.tokens ?? []) {
    if (token.label.toLowerCase().includes(lower) || token.slug.toLowerCase().includes(lower)) {
      results.push({ kind: "token", id: token.id, label: token.label, x: token.x, y: token.y });
    }
  }

  for (const anchor of map.anchors) {
    if (anchor.label.toLowerCase().includes(lower)) {
      results.push({ kind: "anchor", id: anchor.id, label: anchor.label, x: anchor.x, y: anchor.y });
    }
  }

  for (const area of map.areas ?? []) {
    if (area.label && area.label.toLowerCase().includes(lower)) {
      results.push({ kind: "area", id: area.id, label: area.label });
    }
  }

  return results;
}

export interface MapOperation {
  action: "place" | "move" | "remove";
  kind?: "token" | "pin";
  label?: string;
  slug?: string;
  link?: string;
  element?: { slug?: string };
  position?: { x: number; y: number; mode?: string };
  style?: { color?: string; icon?: string };
}

/** Executes Craft-style batch map operations */
export function executeMapBatch(
  map: StoryMapDocument,
  operations: readonly MapOperation[]
): { map: StoryMapDocument; results: Array<{ action: string; label?: string; status: string }> } {
  let currentMap = map;
  const results: Array<{ action: string; label?: string; status: string }> = [];

  for (const op of operations) {
    const slug = op.slug || op.element?.slug || (op.label ? op.label.toLowerCase().replace(/\s+/g, "-") : "");
    if (op.action === "place" && op.position) {
      const res = placeMapToken(currentMap, {
        label: op.label || slug,
        slug,
        x: op.position.x,
        y: op.position.y,
        ...(op.link ? { link: op.link } : {}),
        ...(op.style ? { style: op.style } : {}),
      });
      currentMap = res.map;
      results.push({ action: "place", label: res.token.label, status: "created" });
    } else if (op.action === "move" && op.position && slug) {
      const res = moveMapToken(currentMap, slug, { x: op.position.x, y: op.position.y });
      currentMap = res.map;
      results.push({ action: "move", label: slug, status: res.token ? "moved" : "not_found" });
    } else if (op.action === "remove" && slug) {
      const exists = (currentMap.tokens ?? []).some(token => token.id === slug || token.slug.toLowerCase() === slug.toLowerCase() || token.label.toLowerCase() === slug.toLowerCase());
      if (exists) {
        currentMap = removeMapToken(currentMap, slug);
        results.push({ action: "remove", label: slug, status: "removed" });
      } else {
        results.push({ action: "remove", label: slug, status: "not_found" });
      }
    }
  }

  return { map: currentMap, results };
}
